import React from "react";
import { Checkbox, Box, styled, FormControlLabel } from "@mui/material";

export interface MxCheckProps {
  name: string;
  label?: string;
  value: boolean;
  invalid?: boolean;
  disabled?: boolean;
  positionlabel?: "top" | "left" | "right" | "bottom";
  inputRef?: React.Ref<HTMLInputElement>;
  onChange: (e: { target: { name: string; value: boolean } }) => void;
  onBlur?: (e: any) => void;
  onValueChange?: (e: { target: { name: string; value: boolean } }) => void;
  id?: string;
}

const MxCheck = (props: MxCheckProps) => {
  const {
    name,
    label,
    value,
    invalid = false,
    disabled = false,
    positionlabel = "right",
    inputRef,
    onChange,
    onBlur,
    onValueChange,
    id
  } = props;
  const testIdBase = id || name;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = {
      target: {
        name,
        value: event.target.checked
      }
    };
    onChange(value);
    onValueChange?.(value);
  };

  // Si no hay label, renderizar solo el checkbox sin FormControlLabel
  if (!label) {
    return (
      <AutoSizeCheckboxContainer>
        <Checkbox
          checked={value}
          onChange={handleChange}
          inputRef={inputRef}
          disabled={disabled}
          name={name}
          onBlur={onBlur}
          data-testid={`${testIdBase}-checkbox`}
          sx={{
            padding: 0,
            "&.MuiCheckbox-root": {
              padding: 0
            }
          }}
        />
      </AutoSizeCheckboxContainer>
    );
  }

  // Si hay label, usar la lógica anterior
  return (
    <CheckboxContainer positionlabel={positionlabel} haslabel>
      {positionlabel === "top" && (
        <StyledLabel
          invalid={invalid}
          data-testid={`${testIdBase}-label`}
        >
          {label}
        </StyledLabel>
      )}

      <FormControlLabel
        data-testid={`${testIdBase}-form-control-label`}
        control={
          <Checkbox
            checked={value}
            onChange={handleChange}
            inputRef={inputRef}
            disabled={disabled}
            name={name}
            onBlur={onBlur}
            data-testid={`${testIdBase}-checkbox`}
          />
        }
        label={
          (positionlabel === "right" || positionlabel === "left") && (
            <StyledLabel
              invalid={invalid}
              data-testid={`${testIdBase}-label`}
            >
              {label}
            </StyledLabel>
          )
        }
        labelPlacement={positionlabel === "right" ? "end" : "start"}
      />

      {positionlabel === "bottom" && (
        <StyledLabel
          invalid={invalid}
          data-testid={`${testIdBase}-label`}
        >
          {label}
        </StyledLabel>
      )}
    </CheckboxContainer>
  );
};

export default MxCheck;

// Estilo personalizado para el label
const StyledLabel = styled("span")<{ invalid: boolean }>(({ invalid, theme }) => ({
  fontSize: "12px",
  color: invalid ? theme.palette.error.main : "grey",
  fontFamily: "Inter, sans-serif"
}));

// Contenedor auto-ajustable para checkbox sin label
const AutoSizeCheckboxContainer = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "fit-content",
  height: "fit-content",
  minWidth: "auto",
  minHeight: "auto",
  margin: "auto"
}));

// Estilo para manejar la posición del checkbox respecto al label
const CheckboxContainer = styled(Box, {
  shouldForwardProp: prop => prop !== "positionlabel" && prop !== "haslabel"
})<{
  positionlabel: "top" | "left" | "right" | "bottom";
  haslabel: boolean;
}>(({ positionlabel, haslabel }) => ({
  display: "flex",
  flexDirection:
    positionlabel === "top" || positionlabel === "bottom" ? "column" : "row",
  alignItems:
    positionlabel === "left" || positionlabel === "right" ? "center" : "flex-start",
  justifyContent: positionlabel === "right" ? "space-between" : "flex-start",
  width: haslabel ? "auto" : "fit-content",
  height: haslabel ? "auto" : "fit-content"
}));
